import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { graphBuilderApi } from '../../../services/api';
import ABTabBar from './ABTabBar';
import ABTab from './ABTab';
import useABExecution from './useABExecution';
import { layoutProcessDefinition } from './abLayoutEngine';
import './ProcessABPage.css';

function formatCentral(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZoneName: 'short',
  });
}

const AB_SNN_DEFAULTS = {
  enable_context_distillation: true,
  context_potency_threshold: 0.26,
  snn_execution_mode: 'compiled_full_numpy',
  use_cache: true,
  min_similarity: 0.5,
  similarity_top_k: 150,
  enable_ser: true,
};

let _tabCounter = 0;
function newTab(label) {
  _tabCounter += 1;
  return {
    id: `tab-${Date.now()}-${_tabCounter}`,
    label: label || `Run ${_tabCounter}`,
    input: '',
    settings: { globalSNN: { ...AB_SNN_DEFAULTS }, globalLLM: {}, nodeOverrides: {} },
    isRunning: false,
    executionId: null,
    executionState: null,
    nodeResults: null,
  };
}

function ExecutionWrapper({ children }) {
  const execution = useABExecution();
  return children(execution);
}

export default function ProcessABPage() {
  const { processId } = useParams();
  const navigate = useNavigate();

  const [definition, setDefinition] = useState(null);
  const [tabs, setTabs] = useState(() => [newTab('Run A')]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [abSessionId] = useState(() => crypto.randomUUID());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySessions, setHistorySessions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Set initial active tab
  useEffect(() => {
    if (!activeTabId && tabs.length > 0) {
      setActiveTabId(tabs[0].id);
    }
  }, [tabs, activeTabId]);

  // Load process definition
  useEffect(() => {
    if (!processId) return;
    setLoading(true);
    graphBuilderApi.getProcessDefinition(processId)
      .then((data) => setDefinition(data.definition || data))
      .catch((e) => console.error('Failed to load process definition', e))
      .finally(() => setLoading(false));
  }, [processId]);

  // Auto-detect running executions on mount and reconnect
  const autoDetectDone = useRef(false);
  useEffect(() => {
    if (!processId || autoDetectDone.current) return;
    autoDetectDone.current = true;
    graphBuilderApi.listABRuns(processId).then((resp) => {
      const sessions = resp.sessions || [];
      const runningSession = sessions.find((s) =>
        s.tabs.some((t) => t.status === 'running'),
      );
      if (!runningSession) return;
      const runningTabs = runningSession.tabs.map((t, i) => ({
        id: `running-${t.execution_id || i}`,
        label: t.tab_label || `Tab ${i + 1}`,
        input: '',
        settings: { globalSNN: { ...AB_SNN_DEFAULTS }, globalLLM: {}, nodeOverrides: {} },
        isRunning: t.status === 'running',
        executionId: t.execution_id,
        executionState: null,
        nodeResults: null,
      }));
      setTabs(runningTabs);
      setActiveTabId(runningTabs[0].id);
    }).catch((e) => console.error('Failed to check for running executions', e));
  }, [processId]);

  // Precompute map nodes for settings modal
  const mapNodes = useMemo(() => {
    if (!definition) return [];
    try {
      const { nodes } = layoutProcessDefinition(definition);
      return nodes;
    } catch (e) {
      console.error('Failed to layout process definition:', e);
      return [];
    }
  }, [definition]);

  // Extract unique graph IDs referenced in the process definition
  const processGraphIds = useMemo(() => {
    const nodes = definition?.definition?.nodes || definition?.nodes || [];
    const ids = new Set();
    nodes.forEach(n => {
      const gid = n.snn_config?.graph_id || n.graph_id;
      if (gid) ids.add(gid);
      const rid = n.snn_config?.router_graph_id;
      if (rid) ids.add(rid);
    });
    return [...ids];
  }, [definition]);

  // Graph caching
  const [cacheStatus, setCacheStatus] = useState('idle');
  const [cacheProgress, setCacheProgress] = useState({ done: 0, total: 0 });
  const cacheResetTimer = useRef(null);

  const handleCacheAllGraphs = useCallback(async () => {
    if (!processGraphIds.length || cacheStatus === 'caching') return;
    setCacheStatus('caching');
    setCacheProgress({ done: 0, total: processGraphIds.length });
    let failed = false;
    for (let i = 0; i < processGraphIds.length; i++) {
      try {
        await graphBuilderApi.cacheGraph(processGraphIds[i]);
      } catch (err) {
        console.error(`Cache failed for graph ${processGraphIds[i]}:`, err);
        failed = true;
      }
      setCacheProgress({ done: i + 1, total: processGraphIds.length });
    }
    setCacheStatus(failed ? 'error' : 'done');
    if (cacheResetTimer.current) clearTimeout(cacheResetTimer.current);
    cacheResetTimer.current = setTimeout(() => setCacheStatus('idle'), 4000);
  }, [processGraphIds, cacheStatus]);

  useEffect(() => () => {
    if (cacheResetTimer.current) clearTimeout(cacheResetTimer.current);
  }, []);

  // Tab management
  const addTab = useCallback(() => {
    const t = newTab();
    setTabs((prev) => [...prev, t]);
    setActiveTabId(t.id);
  }, []);

  const closeTab = useCallback((id) => {
    let replacementId = null;
    let siblingId = null;
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      const next = prev.filter((t) => t.id !== id);
      if (next.length === 0) {
        const t = newTab('Run A');
        replacementId = t.id;
        return [t];
      }
      siblingId = next[Math.min(idx, next.length - 1)]?.id || next[0]?.id;
      return next;
    });
    setActiveTabId((prev) => {
      if (replacementId) return replacementId;
      if (prev !== id) return prev;
      return siblingId || prev;
    });
  }, []);

  const renameTab = useCallback((id, label) => {
    setTabs((prev) => prev.map((t) => t.id === id ? { ...t, label } : t));
  }, []);

  const updateTabField = useCallback((tabId, field, value) => {
    setTabs((prev) => prev.map((t) => t.id === tabId ? { ...t, [field]: value } : t));
  }, []);

  const onExecutionStarted = useCallback((tabId, executionId) => {
    setTabs((prev) => prev.map((t) =>
      t.id === tabId ? { ...t, executionId, isRunning: true } : t,
    ));
  }, []);

  const onExecutionFinished = useCallback((tabId) => {
    setTabs((prev) => prev.map((t) =>
      t.id === tabId ? { ...t, isRunning: false } : t,
    ));
  }, []);

  // History
  const loadHistory = useCallback(async () => {
    if (!processId) return;
    try {
      const resp = await graphBuilderApi.listABRuns(processId);
      setHistorySessions(resp.sessions || []);
    } catch (e) {
      console.error('Failed to load AB history', e);
    }
  }, [processId]);

  const openHistory = useCallback(() => {
    setHistoryOpen(true);
    loadHistory();
  }, [loadHistory]);

  const restoreSession = useCallback((session) => {
    const sessionTabs = session?.tabs || [];
    if (sessionTabs.length === 0) return;
    const restoredTabs = sessionTabs.map((t, i) => ({
      id: `restored-${t.execution_id || i}`,
      label: t.tab_label || `Tab ${i + 1}`,
      input: '',
      settings: { globalSNN: { ...AB_SNN_DEFAULTS }, globalLLM: {}, nodeOverrides: {} },
      isRunning: t.status === 'running',
      executionId: t.execution_id,
      executionState: null,
      nodeResults: null,
    }));
    setTabs(restoredTabs);
    setActiveTabId(restoredTabs[0].id);
    setHistoryOpen(false);
  }, []);

  const activeTab = tabs.find((t) => t.id === activeTabId);

  if (loading) {
    return (
      <div className="ab-page loading">
        <div className="spinner" />
        <p>Loading process definition...</p>
      </div>
    );
  }

  return (
    <div className="ab-page">
      {/* Header */}
      <div className="ab-page-header">
        <button className="ab-back-btn" onClick={() => navigate(-1)}>&larr; Back</button>
        <h1>Run Process A/B</h1>
        <button className="ab-history-btn" onClick={openHistory}>History</button>
        <button
          className={`ab-cache-btn ${cacheStatus}`}
          onClick={handleCacheAllGraphs}
          disabled={cacheStatus === 'caching' || processGraphIds.length === 0}
          title={processGraphIds.length === 0 ? 'No graphs referenced in this process' : `Cache ${processGraphIds.length} graph(s)`}
        >
          {cacheStatus === 'caching' && <span className="spinner-small" />}
          {cacheStatus === 'idle' && `Cache All Graphs (${processGraphIds.length})`}
          {cacheStatus === 'caching' && `Caching ${cacheProgress.done}/${cacheProgress.total}...`}
          {cacheStatus === 'done' && 'Cached'}
          {cacheStatus === 'error' && 'Cache (partial)'}
        </button>
      </div>

      {/* Tab bar */}
      <ABTabBar
        tabs={tabs}
        activeTabId={activeTabId}
        onSelect={setActiveTabId}
        onAdd={addTab}
        onClose={closeTab}
        onRename={renameTab}
      />

      {/* Active tab content */}
      {activeTab && (
        <ExecutionWrapper key={activeTab.id}>
          {(execution) => (
            <ABTab
              tab={activeTab}
              definition={definition}
              processId={processId}
              abSessionId={abSessionId}
              onInputChange={(val) => updateTabField(activeTab.id, 'input', val)}
              onSettingsChange={(s) => updateTabField(activeTab.id, 'settings', s)}
              onExecutionStarted={(execId) => onExecutionStarted(activeTab.id, execId)}
              onExecutionFinished={() => onExecutionFinished(activeTab.id)}
              execution={execution}
              mapNodes={mapNodes}
            />
          )}
        </ExecutionWrapper>
      )}

      {/* History dropdown */}
      {historyOpen && (
        <div className="ab-history-overlay" onClick={() => setHistoryOpen(false)}>
          <div className="ab-history-panel" onClick={(e) => e.stopPropagation()}>
            <div className="history-header">
              <h2>Past A/B Sessions</h2>
              <button className="modal-close" onClick={() => setHistoryOpen(false)}>&times;</button>
            </div>
            <div className="history-list">
              {historySessions.length === 0 && <p className="history-empty">No past sessions found.</p>}
              {historySessions.map((session, idx) => (
                <div
                  key={session.ab_session_id || idx}
                  className="history-item"
                  onClick={() => restoreSession(session)}
                >
                  <div className="history-session-id">
                    <span className="history-uuid-label">Session:</span>
                    <code
                      className="history-uuid"
                      title="Click to copy"
                      onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(session.ab_session_id || ''); }}
                    >
                      {session.ab_session_id || 'unknown'}
                    </code>
                  </div>
                  <div className="history-tabs">
                    {(session.tabs || []).map((t, ti) => (
                      <div key={t.execution_id || ti} className="history-tab-row">
                        <span className={`history-tab-pill ${t.status || 'pending'}`}>
                          {t.tab_label || 'Tab'}
                        </span>
                        {t.execution_id && (
                          <code
                            className="history-uuid"
                            title="Click to copy execution ID"
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(t.execution_id); }}
                          >
                            {t.execution_id}
                          </code>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="history-time">{formatCentral((session.tabs || [])[0]?.started_at)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
